import moment from "moment"

export default new (class DateUtils {
  isCurrentYear(date, useUtc = false) {
    let currentYear = useUtc ? moment.utc().local().format("YYYY") : moment().format("YYYY")
    let year = useUtc ? moment.utc(date).local().format("YYYY") : moment(date).format("YYYY")
    if (currentYear === year) {
      return true
    } else {
      return false
    }
  }

  isCurrentDate(date, useUtc = false) {
    let currentDate = useUtc ? moment.utc().local().format("DD/MM/YYYY") : moment().format("DD/MM/YYYY")
    let otherDate = useUtc ? moment.utc(date).local().format("DD/MM/YYYY") : moment(date).format("DD/MM/YYYY")

    return currentDate === otherDate
  }

  getMinutes(date, useUtc = false) {
    let currentDate = useUtc ? moment.utc().local() : moment()
    let otherDate = useUtc ? moment.utc(date).local() : moment(date)

    return moment(currentDate).diff(moment(otherDate), "minutes")
  }

  /**
   * Formats a date according to the specified format string
   *
   * @param {string|Date|moment.Moment} date - The date to format. Can be a date string, Date object, or moment instance
   * @param {string|null} [format=null] - The output format string using moment.js format tokens (e.g., 'YYYY-MM-DD', 'MMM Do, YYYY')
   * @param {boolean} [convertToLocal=false] - Whether to convert to local time.
   * @param {boolean} [forceYearDisplay=false] - When format is 'MMM Do, YYYY', forces year display even for current year dates
   * @param {string|null} [inputFormat=null] - The format of the input date string for parsing. Uses moment's default parsing if null
   * @returns {string} The formatted date string, or empty string if date is invalid or not provided
   *
   * @example
   * // Basic formatting
   * formatDate('2023-12-25', 'MMM Do, YYYY') // "Dec 25th, 2023"
   *
   * @example
   * // UTC conversion for API dates
   * formatDate('2023-12-25T15:30:00Z', 'YYYY-MM-DD HH:mm', true) // "2023-12-25 10:30" (assuming EST timezone)
   *
   * @example
   * // Date-only treatment of UTC datetime strings
   * formatDate('2023-12-25T00:00:00.000000Z', 'MMM Do, YYYY', false) // "Dec 25th, 2023" (treats as date-only)
   *
   * @example
   * // Current year handling - hides year for current year unless forced
   * formatDate('2024-03-15', 'MMM Do, YYYY') // "Mar 15th" (if current year is 2024)
   * formatDate('2024-03-15', 'MMM Do, YYYY', false, true) // "Mar 15th, 2024" (forced year display)
   *
   * @example
   * // Formats starting with "MMM Do, YYYY" may include a suffix (e.g. time).
   * // The same current-year rule still applies.
   * formatDate('2026-03-15 14:30:00', 'MMM Do, YYYY, h:mm A') // "Mar 15th, 2:30 PM"
   * formatDate('2025-03-15 14:30:00', 'MMM Do, YYYY, h:mm A') // "Mar 15th, 2025, 2:30 PM"
   */
  formatDate(date, format = "MMM Do, YYYY", convertToLocal = false, forceYearDisplay = false, inputFormat = null) {
    if (!date) {
      return ""
    }

    const hasOffset = /Z$|[+-]\d\d:?\d\d$/.test(date)

    let instance

    if (convertToLocal) {
      //return date in browser local time
      //If an offset is already present - parse and then shift to local
      //If no offset - assume the value represents UTC, then shift to local
      instance = (hasOffset ? moment(date, inputFormat) : moment.utc(date, inputFormat)).local()
    } else {
      //return date with no conversion
      //If date has offset - ignore offset
      //If no offset - treat the string as local time
      instance = hasOffset ? moment.parseZone(date, inputFormat) : moment(date, inputFormat)
    }

    if (!instance.isValid()) {
      return ""
    }

    const isMonthDayYearFormat = format === "MMM Do, YYYY" || format.startsWith("MMM Do, YYYY,")
    if (isMonthDayYearFormat) {
      const suffix = format === "MMM Do, YYYY" ? "" : format.slice("MMM Do, YYYY".length)
      const now = moment()
      if (instance.isSame(now, "year") && !forceYearDisplay) {
        return instance.format("MMM Do" + suffix)
      } else {
        return instance.format("MMM Do, YYYY" + suffix)
      }
    }

    if (format === "MM/DD/YYYY" && !this.isAmericanLocale()) {
      format = "DD/MM/YYYY"
    }

    // Use the provided format for all other cases
    return instance.format(format)
  }

  isAmericanLocale() {
    try {
      const locale = navigator.language || navigator.userLanguage
      return locale.startsWith("en-US")
    } catch (e) {
      console.error(e)
      return false
    }
  }
})()
